import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import SignatureCapture from '@/components/SignatureCapture';

interface Document {
  id: string;
  title: string;
  content?: string;
  status: string;
  createdAt: string;
  completedAt?: string;
}

interface Participant {
  id: string;
  role?: string;
  signatureStatus: string;
  signedAt?: string;
  model: {
    id: string;
    legalName: string;
    stageNames: string[];
  };
}

interface Signature {
  id: string;
  type: string;
  capturedAt: string;
  verified: boolean;
}

interface Model {
  id: string;
  legalName: string;
  stageNames?: string[];
  email?: string;
}

export default function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // Modal states
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showSignatureCapture, setShowSignatureCapture] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [modelSearch, setModelSearch] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [participantRole, setParticipantRole] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () =>
      api.get<{
        document: Document;
        participants: Participant[];
        signatures: Signature[];
      }>(`/documents/${id}`),
  });

  // Search for models to add as participants
  const { data: searchResults } = useQuery({
    queryKey: ['models-search', modelSearch],
    queryFn: () => api.get<{ models: Model[] }>(`/models/search?q=${encodeURIComponent(modelSearch)}`),
    enabled: modelSearch.length >= 2,
  });

  const sendMutation = useMutation({
    mutationFn: () => api.post(`/documents/${id}/send`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document', id] }),
  });

  const voidMutation = useMutation({
    mutationFn: () => api.post(`/documents/${id}/void`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document', id] }),
  });

  const addParticipantMutation = useMutation({
    mutationFn: (data: { modelProfileId: string; role?: string }) =>
      api.post(`/documents/${id}/participants`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      setShowAddParticipant(false);
      setModelSearch('');
      setSelectedModelId('');
      setParticipantRole('');
    },
  });

  const removeParticipantMutation = useMutation({
    mutationFn: (participantId: string) =>
      api.delete(`/documents/${id}/participants/${participantId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document', id] }),
  });

  const captureSignatureMutation = useMutation({
    mutationFn: (data: {
      documentId: string;
      participantId: string;
      type: string;
      signatureData: string;
    }) => api.post('/signatures/capture', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      setShowSignatureCapture(false);
      setSelectedParticipant(null);
    },
  });

  const handleSignatureCapture = (sigData: { type: string; data: string }) => {
    if (!selectedParticipant || !id) return;

    captureSignatureMutation.mutate({
      documentId: id,
      participantId: selectedParticipant.id,
      type: sigData.type,
      signatureData: sigData.data,
    });
  };

  const handleAddParticipant = () => {
    if (!selectedModelId) return;
    addParticipantMutation.mutate({
      modelProfileId: selectedModelId,
      role: participantRole || undefined,
    });
  };

  if (isLoading) {
    return <div className="loading">Loading document...</div>;
  }

  const document = data?.data.document;
  const participants = data?.data.participants || [];
  const signatures = data?.data.signatures || [];
  const models = searchResults?.data.models || [];

  if (!document) {
    return <div className="error">Document not found</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{document.title || 'Untitled Document'}</h1>
          <p className="page-subtitle">
            Created {new Date(document.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="header-actions">
          {document.status === 'draft' && (
            <button
              onClick={() => sendMutation.mutate()}
              className="btn btn-primary"
              disabled={sendMutation.isPending || participants.length === 0}
            >
              Send for Signing
            </button>
          )}
          {document.status !== 'voided' && document.status !== 'completed' && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to void this document? This action cannot be undone.')) {
                  voidMutation.mutate();
                }
              }}
              className="btn btn-danger"
              disabled={voidMutation.isPending}
            >
              Void Document
            </button>
          )}
          {document.status === 'completed' && (
            <a
              href={`/api/documents/${id}/download`}
              className="btn btn-primary"
              download
            >
              Download PDF
            </a>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="card">
          <h3>Document Status</h3>
          <span className={`badge badge-large badge-${document.status}`}>
            {document.status.replace('_', ' ')}
          </span>
          {document.completedAt && (
            <p className="status-date">
              Completed: {new Date(document.completedAt).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Participants & Signatures</h3>
          {document.status === 'draft' && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowAddParticipant(true)}
            >
              Add Participant
            </button>
          )}
        </div>

        {participants.length === 0 ? (
          <p className="empty-text">
            No participants added. Add participants before sending for signing.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Signature Status</th>
                <th>Signed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.id}>
                  <td>{participant.model.legalName}</td>
                  <td>{participant.role || '-'}</td>
                  <td>
                    <span
                      className={`badge ${participant.signatureStatus === 'signed' ? 'badge-success' : 'badge-warning'}`}
                    >
                      {participant.signatureStatus}
                    </span>
                  </td>
                  <td>
                    {participant.signedAt
                      ? new Date(participant.signedAt).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    {document.status === 'pending_signatures' &&
                      participant.signatureStatus !== 'signed' && (
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => {
                            setSelectedParticipant(participant);
                            setShowSignatureCapture(true);
                          }}
                        >
                          Capture Signature
                        </button>
                      )}
                    {document.status === 'draft' && (
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => {
                          if (window.confirm(`Remove ${participant.model.legalName} from this document?`)) {
                            removeParticipantMutation.mutate(participant.id);
                          }
                        }}
                        disabled={removeParticipantMutation.isPending}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {signatures.length > 0 && (
        <div className="card">
          <h3>Captured Signatures</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Captured At</th>
                <th>Verified</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((sig) => (
                <tr key={sig.id}>
                  <td>{sig.type}</td>
                  <td>{new Date(sig.capturedAt).toLocaleString()}</td>
                  <td>
                    <span
                      className={`badge ${sig.verified ? 'badge-success' : 'badge-warning'}`}
                    >
                      {sig.verified ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {document.content && (
        <div className="card">
          <h3>Document Preview</h3>
          <div className="document-preview">
            <pre>{document.content}</pre>
          </div>
        </div>
      )}

      {/* Add Participant Modal */}
      {showAddParticipant && (
        <div className="modal-overlay" onClick={() => setShowAddParticipant(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Participant</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddParticipant(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="model-search">Search for Model</label>
                <input
                  type="text"
                  id="model-search"
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  placeholder="Search by name or email..."
                />
              </div>

              {models.length > 0 && (
                <div className="form-group">
                  <label>Select Model</label>
                  <div className="model-list">
                    {models.map((model) => (
                      <label key={model.id} className="model-option">
                        <input
                          type="radio"
                          name="selectedModel"
                          value={model.id}
                          checked={selectedModelId === model.id}
                          onChange={(e) => setSelectedModelId(e.target.value)}
                        />
                        <span>
                          {model.legalName}
                          {model.stageNames?.length ? ` (${model.stageNames[0]})` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {selectedModelId && (
                <div className="form-group">
                  <label htmlFor="participant-role">Role (Optional)</label>
                  <input
                    type="text"
                    id="participant-role"
                    value={participantRole}
                    onChange={(e) => setParticipantRole(e.target.value)}
                    placeholder="e.g., Performer, Model, Witness"
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddParticipant(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddParticipant}
                disabled={!selectedModelId || addParticipantMutation.isPending}
              >
                {addParticipantMutation.isPending ? 'Adding...' : 'Add Participant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Capture Modal */}
      {showSignatureCapture && selectedParticipant && (
        <div className="modal-overlay" onClick={() => setShowSignatureCapture(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Capture Signature - {selectedParticipant.model.legalName}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowSignatureCapture(false);
                  setSelectedParticipant(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <SignatureCapture
                onCapture={handleSignatureCapture}
                onCancel={() => {
                  setShowSignatureCapture(false);
                  setSelectedParticipant(null);
                }}
              />
              {captureSignatureMutation.isPending && (
                <div className="loading-overlay">
                  <p>Saving signature...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-large {
          max-width: 700px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
          line-height: 1;
        }

        .modal-body {
          padding: 20px;
          position: relative;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .model-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 8px;
        }

        .model-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
        }

        .model-option:hover {
          background: #f3f4f6;
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
}
